/**
 * The narrated tour of a result: what is playing, and which panel it is about.
 *
 * The control lives in the rail and the highlight lives on the Result page, which are siblings
 * under the Shell, so the state that joins them sits above both. The page registers the run it is
 * showing; the rail offers Play only once there is one.
 *
 * Three things here are less obvious than they look.
 *
 * **The audio element is unlocked inside the click.** Safari and iOS only allow playback that
 * starts in a user gesture, and fetching seven clips takes a moment - by the time they arrive the
 * gesture is over and `play()` is refused. Playing a silent frame synchronously on the press keeps
 * the element unlocked for the real audio that follows.
 *
 * **A tour with no sound still runs.** If speech is unavailable the captions play on a timer, so
 * the highlight still walks the page and the words are still readable. That is also what somebody
 * with the sound off gets, which is most people.
 *
 * **Leaving stops it.** Navigating to another run while a voice is describing the last one would be
 * worse than silence.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/lib/queries";

export type Clip = Schemas["NarrationClip"];

// A silent MP3 frame. Played on the press so the element is allowed to speak a moment later.
const SILENCE =
  "data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA" +
  "gICAgICAgICAgICAgICAgICAgICAgID/////////////////////////////////AAAAOkxBTUUzLjEwMAGuAAAAAAAAAAAUgJAU";

// How long a caption stays up when there is no audio to time it. Roughly reading pace.
const READING_MS = (text: string) => Math.max(2600, Math.min(12000, text.length * 55));

type Status = "idle" | "loading" | "playing" | "paused" | "error";

/** Where a tour comes from: somebody's own run, or a published example anyone may read. */
type Source =
  | { kind: "run"; studyId: string; runId: string }
  | { kind: "published"; slug: string };

interface Tour {
  status: Status;
  clips: Clip[];
  step: number;
  /** The panel being spoken about, or "" for the opening passage. Empty when nothing is playing. */
  activeKey: string;
  /** Why there is no sound, when there is none. The tour still runs. */
  audioError: string | null;
  /** Whether a finished result is on screen for this to be about. */
  available: boolean;
  start: () => void;
  pause: () => void;
  resume: () => void;
  jump: (step: number) => void;
  stop: () => void;
  attach: (studyId: string | undefined, runId: string | undefined) => void;
  /** The same tour, for a published example read without signing in. */
  attachPublished: (slug: string | undefined) => void;
}

const TourContext = createContext<Tour | null>(null);

export function useTour(): Tour {
  const tour = useContext(TourContext);
  if (!tour) throw new Error("useTour outside TourProvider");
  return tour;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>("idle");
  const [clips, setClips] = useState<Clip[]>([]);
  const [step, setStep] = useState(0);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [run, setRun] = useState<Source | null>(null);

  const audio = useRef<HTMLAudioElement | null>(null);
  const urls = useRef<string[]>([]);
  const timer = useRef<number | null>(null);

  const clearTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const stop = useCallback(() => {
    clearTimer();
    audio.current?.pause();
    setStatus("idle");
    setStep(0);
  }, []);

  const attach = useCallback((studyId: string | undefined, runId: string | undefined) => {
    setRun(studyId && runId ? { kind: "run", studyId, runId } : null);
  }, []);

  const attachPublished = useCallback((slug: string | undefined) => {
    setRun(slug ? { kind: "published", slug } : null);
  }, []);

  // A different run, or none: whatever was playing is no longer what is on screen.
  useEffect(() => {
    stop();
    setClips([]);
    setAudioError(null);
    urls.current.forEach(URL.revokeObjectURL);
    urls.current = [];
  }, [run, stop]);

  useEffect(() => () => urls.current.forEach(URL.revokeObjectURL), []);

  const play = useCallback(
    (index: number, loaded: Clip[]) => {
      const clip = loaded[index];
      if (!clip) {
        stop();
        return;
      }
      setStep(index);
      setStatus("playing");
      clearTimer();
      const url = urls.current[index];
      if (url && audio.current) {
        audio.current.src = url;
        void audio.current.play().catch(() => {
          // Refused after all: fall back to the timed captions rather than stalling on silence.
          timer.current = window.setTimeout(() => play(index + 1, loaded), READING_MS(clip.text));
        });
      } else {
        timer.current = window.setTimeout(() => play(index + 1, loaded), READING_MS(clip.text));
      }
    },
    [stop],
  );

  const start = useCallback(() => {
    if (!run) return;
    // Inside the gesture, before any awaiting: this is what keeps the element allowed to speak.
    if (!audio.current) audio.current = new Audio();
    audio.current.src = SILENCE;
    void audio.current.play().catch(() => undefined);

    if (clips.length > 0) {
      play(0, clips);
      return;
    }
    setStatus("loading");
    void (async () => {
      try {
        const body = await unwrap(
          run.kind === "published"
            ? await api.GET("/api/v1/showcase/{slug}/narration", { params: { path: { slug: run.slug } } })
            : await api.GET("/api/v1/studies/{study_id}/runs/{run_id}/narration", {
                params: { path: { study_id: run.studyId, run_id: run.runId } },
              }),
        );
        urls.current = body.clips.map((clip) =>
          clip.audio ? URL.createObjectURL(mp3(clip.audio)) : "",
        );
        setClips(body.clips);
        setAudioError(body.audio_error ?? null);
        play(0, body.clips);
      } catch (err: unknown) {
        setAudioError(err instanceof Error ? err.message : "the tour could not be loaded");
        setStatus("error");
      }
    })();
  }, [run, clips, play]);

  const pause = useCallback(() => {
    clearTimer();
    audio.current?.pause();
    setStatus("paused");
  }, []);

  const resume = useCallback(() => {
    setStatus("playing");
    if (audio.current?.src && !audio.current.ended) {
      void audio.current.play().catch(() => undefined);
      return;
    }
    play(step, clips);
  }, [play, step, clips]);

  const jump = useCallback(
    (to: number) => {
      if (clips.length === 0) return;
      play(Math.max(0, Math.min(to, clips.length - 1)), clips);
    },
    [clips, play],
  );

  // One clip ending is what moves the tour on, so the handler follows whatever `play` is current.
  useEffect(() => {
    const element = audio.current;
    if (!element) return;
    const onEnded = () => play(step + 1, clips);
    element.addEventListener("ended", onEnded);
    return () => element.removeEventListener("ended", onEnded);
  }, [play, step, clips]);

  const value = useMemo<Tour>(
    () => ({
      status,
      clips,
      step,
      activeKey: status === "playing" || status === "paused" ? (clips[step]?.key ?? "") : "",
      audioError,
      available: run !== null,
      start,
      pause,
      resume,
      jump,
      stop,
      attach,
      attachPublished,
    }),
    [status, clips, step, audioError, run, start, pause, resume, jump, stop, attach, attachPublished],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/** Tell the tour which finished result is on screen, so the rail knows whether to offer Play. */
export function useNarrated(studyId: string | undefined, runId: string | undefined, ready: boolean): void {
  const { attach } = useTour();
  useEffect(() => {
    attach(ready ? studyId : undefined, ready ? runId : undefined);
    return () => attach(undefined, undefined);
  }, [attach, studyId, runId, ready]);
}

/** The same, for a published example. */
export function useNarratedExample(slug: string | undefined, ready: boolean): void {
  const { attachPublished } = useTour();
  useEffect(() => {
    attachPublished(ready ? slug : undefined);
    return () => attachPublished(undefined);
  }, [attachPublished, slug, ready]);
}


function mp3(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "audio/mpeg" });
}
